import { Badge } from "@thilke/ui/components/badge";
import {
  Status,
  StatusIndicator,
  StatusLabel,
} from "@thilke/ui/components/ui/status";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface ChatCardProps extends React.ComponentProps<"article"> {
  title: string;
  subtitle?: string;
  speaker?: string;
  tone?: "default" | "success" | "warning" | "danger";
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

const toneClass: Record<NonNullable<ChatCardProps["tone"]>, string> = {
  default: "border-border",
  success: "border-success/60",
  warning: "border-warning/70",
  danger: "border-destructive/70",
};

const statusVariant: Record<
  NonNullable<ChatCardProps["tone"]>,
  React.ComponentProps<typeof Status>["variant"]
> = {
  default: "default",
  success: "success",
  warning: "warning",
  danger: "error",
};

function ChatCard({
  title,
  subtitle,
  speaker,
  tone = "default",
  meta,
  actions,
  children,
  className,
  ...props
}: ChatCardProps) {
  return (
    <article
      data-slot="chat-card"
      className={cn(
        "grid gap-2.5 rounded-md border bg-card p-3 text-card-foreground shadow-sm",
        toneClass[tone],
        className,
      )}
      {...props}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="truncate text-sm font-semibold leading-tight">
              {title}
            </h3>
            {speaker ? <Badge variant="outline">{speaker}</Badge> : null}
            <Status variant={statusVariant[tone]}>
              <StatusIndicator />
              <StatusLabel>{tone === "danger" ? "critical" : tone}</StatusLabel>
            </Status>
          </div>
          {subtitle ? (
            <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        {meta ? (
          <div className="shrink-0 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground">
            {meta}
          </div>
        ) : null}
      </header>
      {children ? (
        <div className="rounded-md border border-border bg-background/70 p-2.5 text-sm leading-normal text-foreground/90">
          {children}
        </div>
      ) : null}
      {actions ? (
        <footer className="flex flex-wrap items-center justify-end gap-1">
          {actions}
        </footer>
      ) : null}
    </article>
  );
}

export { ChatCard };
